import axios from 'axios';
import { baseUrl } from '../utils/config';

export interface CartItem {
  id: number;
  product_id: string;
  quantity: number;
  product: any;
}

export interface Cart {
  id: number;
  utilisateur_id: number;
  items: CartItem[];
}

const api = axios.create({ baseURL: baseUrl });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

export const CartService = {
  /**
   * Get the cart for a specific user by user_id
   */
  async getCartByUser(userId: number): Promise<Cart> {
    try {
      // console.log(`[CartService] Fetching cart for userId: ${userId}`);
      const response = await api.get<Cart>(`/cart/${userId}`);

      if (!response.data) {
        console.warn(`[CartService] Empty response for userId: ${userId}`);
        return { id: 0, utilisateur_id: userId, items: [] };
      }

      // Ensure items array exists
      if (!response.data.items || !Array.isArray(response.data.items)) {
        console.warn(`[CartService] Invalid items format for userId: ${userId}`, response.data);
        return { ...response.data, items: [] };
      }

      console.log(`[CartService] Successfully fetched cart for userId: ${userId}`, response.data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.log(`[CartService] No cart found for userId: ${userId}, creating empty cart`);
        return { id: 0, utilisateur_id: userId, items: [] };
      }

      console.error(`[CartService] Error fetching cart for userId: ${userId}`, error);
      throw error;
    }
  },

  /**
   * Add an item to the user's cart
   */
  async addToCart(userId: number, product_id: string, quantity: number = 1): Promise<Cart> {
    try {
      // console.log(`[CartService] Adding to cart - userId: ${userId}, product_id: ${product_id}, quantity: ${quantity}`);
      const response = await api.post<Cart>(
        `/cart/${userId}/add`,
        { product_id, quantity }
      );
      console.log(`[CartService] Successfully added to cart - userId: ${userId}`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[CartService] Error adding to cart - userId: ${userId}, product_id: ${product_id}`, error);
      throw error;
    }
  },

  /**
   * Remove an item from the user's cart
   */
  async removeFromCart(userId: number, product_id: string): Promise<Cart> {
    try {
      // console.log(`[CartService] Removing from cart - userId: ${userId}, product_id: ${product_id}`);
      const encodedProductId = encodeURIComponent(product_id);
      const response = await api.delete<Cart>(
        `/cart/${userId}/remove/${encodedProductId}`
      );
      console.log(`[CartService] Successfully removed from cart - userId: ${userId}`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[CartService] Error removing from cart - userId: ${userId}, product_id: ${product_id}`, error);
      throw error;
    }
  },
};